import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardPath } from '../../utils/userUtils';
import { safePostMessage } from '../../utils/securityUtils';
import { trackUserLogin } from '../../utils/analytics';
import './AuthCallback.css';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple calls in the same component instance
      if (hasStarted) {
        console.log('⏭️ Callback already started, skipping...');
        return;
      }
      
      setHasStarted(true);
      
      try {
        // Add immediate logging to see if callback is triggered
        console.log('🎯 AuthCallback component mounted!');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);
        console.log('Is popup window:', window.opener !== null);
        
        // Check for Chrome DevTools interference
        if (authService.checkChromeDevToolsInterference()) {
          console.log('🟡 Chrome DevTools interference detected!');
          authService.recoverFromChromeInterference();
          return;
        }
        
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        console.log('=== OAuth Callback Debug ===');
        console.log('Authorization code:', code);
        console.log('State:', state);
        console.log('Error:', error);
        console.log('Error description:', errorDescription);
        console.log('Full URL:', window.location.href);

        // Check for OAuth errors
        if (error) {
          console.error('OAuth error:', error, errorDescription);
          
          // If this is a popup, notify parent window
          if (window.opener) {
            console.log('🟡 Notifying parent window of OAuth error...');
            const messageSent = safePostMessage(window.opener, {
              type: 'OAUTH2_ERROR',
              error: errorDescription || 'Authentication failed'
            });
            if (messageSent) {
              window.close();
            } else {
              console.warn('Failed to send error message to parent window');
            }
            return;
          }
          
          setError(errorDescription || 'Authentication failed');
          setIsProcessing(false);
          return;
        }

        // Check for authorization code
        if (!code) {
          console.error('No authorization code found in URL parameters');
          
          // If this is a popup, notify parent window
          if (window.opener) {
            console.log('🟡 Notifying parent window of missing code...');
            const messageSent = safePostMessage(window.opener, {
              type: 'OAUTH2_ERROR',
              error: 'Authorization code not found'
            });
            if (messageSent) {
              window.close();
            } else {
              console.warn('Failed to send missing code error to parent window');
            }
            return;
          }
          
          setError('Authorization code not found');
          setIsProcessing(false);
          return;
        }

        console.log('Attempting token exchange with code:', code);
        
        // Exchange code for tokens and get user info
        const user = await authService.handleCallback(code, state || undefined);
        console.log('Token exchange completed, user data:', user);

        // Track login event via Google Analytics
        trackUserLogin('google_oauth');

        // Check if tokens were actually stored
        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        console.log('Tokens in localStorage - Access:', !!accessToken, 'Refresh:', !!refreshToken);

        // If this is a popup window, notify the parent window and close
        if (window.opener) {
          console.log('🟡 This is a popup window, notifying parent of success...');
          const messageSent = safePostMessage(window.opener, {
            type: 'OAUTH2_SUCCESS',
            user: user
          });
          
          // Close the popup after a short delay
          setTimeout(() => {
            if (messageSent) {
              window.close();
            } else {
              console.warn('Failed to send success message to parent window');
            }
          }, 500);
          return;
        }

        console.log('Setting user in auth context...');
        // Use refreshUser to update the context
        await refreshUser();

        // Get the redirect URL that was saved before login
        const postLoginRedirect = localStorage.getItem('post_login_redirect');
        localStorage.removeItem('post_login_redirect');

        // Determine redirect based on user role if no specific redirect was saved
        let redirectPath = postLoginRedirect || '/';
        
        if (!postLoginRedirect || postLoginRedirect === '/') {
          // Use utility function for smart redirect
          redirectPath = getDashboardPath(user);
        }

        console.log('Redirecting to:', redirectPath);
        // Redirect to the determined page
        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error('Callback handling error:', error);
        console.error('Error details:', {
          message: (error as Error).message,
          stack: (error as Error).stack,
          name: (error as Error).name
        });
        
        const errorMessage = (error as Error).message || 'Failed to complete authentication';
        
        // If this is a popup, notify parent window
        if (window.opener) {
          console.log('🟡 Notifying parent window of callback error...');
          const messageSent = safePostMessage(window.opener, {
            type: 'OAUTH2_ERROR',
            error: errorMessage
          });
          if (messageSent) {
            window.close();
          } else {
            console.warn('Failed to send callback error to parent window');
          }
          return;
        }
        
        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshUser]);

  const handleRetry = () => {
    authService.login();
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  if (isProcessing) {
    return (
      <div className="callback-page">
        <div className="callback-container">
          <div className="callback-content">
            <div className="spinner">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="32"
                  strokeDashoffset="32"
                >
                  <animate
                    attributeName="stroke-dasharray"
                    dur="2s"
                    values="0 32;16 16;0 32;0 32"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="stroke-dashoffset"
                    dur="2s"
                    values="0;-16;-32;-32"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <h2>Completing authentication...</h2>
            <p>Please wait while we sign you in.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="callback-page">
        <div className="callback-container">
          <div className="callback-content error">
            <div className="error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h2>Authentication Failed</h2>
            <p>{error}</p>
            <div className="callback-actions">
              <button onClick={handleRetry} className="retry-button">
                Try Again
              </button>
              <button onClick={handleGoHome} className="home-button">
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;